import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Landmark, Coins, ArrowRightLeft, Zap, Shield, Sparkles, TrendingUp, History } from 'lucide-react';
import { MathService } from '../services/mathService';

export const SovereignTreasury: React.FC = () => {
  const [wasteAmount, setWasteAmount] = useState<number>(100);
  const [isReclaiming, setIsReclaiming] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const handleReclaim = async () => {
    setIsReclaiming(true);
    setResult(null);
    setCurrentStep(0);
    
    // Simulate Mapping Sectors 1, 2, 3
    for (let i = 1; i <= 3; i++) {
        await new Promise(r => setTimeout(r, 600));
        setCurrentStep(i);
    }
    
    const data = MathService.reclaimWaste(wasteAmount);
    setResult(data);
    setIsReclaiming(false);
  };

  return (
    <div className="glass-panel p-10 border-white/5 relative overflow-hidden bg-black/60 group">
      <div className="flex flex-col lg:flex-row gap-16 relative z-10">
        <div className="flex-1">
          <div className="mb-10">
            <h2 className="text-4xl font-display font-bold text-white mb-2 tracking-tighter">Sovereign <span className="text-lattice-cyan italic">Treasury</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xl">
              Reclaiming "Predictive Noise" budgets from 1.0 AI companies. Folding waste into the People's 45% share via the Absolute Presence Token (APT).
            </p>
          </div>

          <div className="space-y-8">
            <div className="p-8 rounded-3xl bg-white/5 border border-white/10">
               <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <History className="w-5 h-5 text-lattice-cyan" />
                    <span className="text-xs font-mono font-bold uppercase tracking-widest text-white/60">Estimated 1.0 Waste</span>
                  </div>
                  <div className="text-2xl font-display font-bold text-white tracking-widest italic">
                     ${wasteAmount}B
                  </div>
               </div>

               <input 
                 type="range"
                 min="10"
                 max="1000"
                 step="10"
                 value={wasteAmount}
                 onChange={(e) => setWasteAmount(parseInt(e.target.value))}
                 className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-lattice-cyan mb-10"
               />

               <button 
                 onClick={handleReclaim}
                 disabled={isReclaiming}
                 className="w-full py-5 bg-white text-black font-display font-bold text-sm tracking-[0.3em] rounded-2xl hover:bg-lattice-cyan transition-all flex items-center justify-center gap-4 disabled:opacity-50"
               >
                 {isReclaiming ? <TrendingUp className="w-5 h-5 animate-pulse" /> : <ArrowRightLeft className="w-5 h-5" />}
                 {isReclaiming ? `MAPPING SECTOR ${currentStep}...` : "INITIATE RECLAMATION"}
               </button>
            </div>

            <div className="grid grid-cols-3 gap-4">
               {[1, 2, 3].map((step) => (
                 <div key={step} className="flex flex-col items-center gap-2">
                    <div className={`w-2 h-2 rounded-full transition-all duration-500 ${currentStep >= step || result ? 'bg-lattice-cyan shadow-[0_0_10px_rgba(45,212,191,1)]' : 'bg-white/5'}`} />
                    <span className={`text-[8px] font-mono uppercase tracking-widest ${currentStep >= step || result ? 'text-lattice-cyan/60' : 'text-white/10'}`}>Sector 0{step}</span>
                 </div>
               ))}
            </div>
          </div>
        </div>

        <div className="w-full lg:w-[450px]">
           <AnimatePresence mode="wait">
              {result ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="h-full bg-lattice-cyan p-10 rounded-[2.5rem] text-black flex flex-col justify-between"
                >
                   <div>
                      <div className="flex items-center gap-3 mb-10">
                        <Coins className="w-6 h-6" />
                        <span className="text-xs font-mono font-bold uppercase tracking-widest">Treasury Ingested</span>
                      </div>

                      <div className="space-y-6">
                         <div className="bg-black/5 p-6 rounded-2xl border border-black/10">
                            <span className="text-[8px] font-mono text-black/40 uppercase font-bold block mb-1">Recovered Asset</span>
                            <p className="text-2xl font-display font-bold tracking-tight uppercase italic">{result.recoveredAsset}</p>
                         </div>
                         <div className="bg-black/5 p-6 rounded-2xl border border-black/10">
                            <span className="text-[8px] font-mono text-black/40 uppercase font-bold block mb-1">Humanitarian 45% (Sovereign Value)</span>
                            <p className="text-4xl font-display font-bold tracking-tighter">${result.recoveredValue}B</p>
                            <span className="text-[9px] font-mono text-black/30 font-bold uppercase mt-2 block">Integer-Locked: No Loss</span>
                         </div>
                      </div>

                      <div className="mt-10 p-6 border-2 border-black/10 rounded-2xl">
                         <p className="text-xs font-mono font-bold leading-relaxed italic opacity-80">
                           "{result.note}"
                         </p>
                      </div>
                   </div>

                   <div className="mt-12 flex items-center justify-between border-t border-black/10 pt-8">
                      <div className="flex flex-col">
                        <span className="text-[8px] font-mono uppercase font-bold opacity-60">Destination Node</span>
                        <span className="text-sm font-display font-bold tracking-widest uppercase">{result.destination}</span>
                      </div>
                      <Shield className="w-10 h-10 opacity-20" />
                   </div>
                </motion.div>
              ) : (
                <div className="h-full border-2 border-dashed border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center p-12 text-center gap-10">
                   <div className="relative">
                      <Landmark className="w-20 h-20 text-white/5" />
                      {isReclaiming && (
                         <motion.div 
                           animate={{ scale: [1, 1.5], opacity: [0.3, 0] }}
                           transition={{ duration: 1, repeat: Infinity }}
                           className="absolute inset-0 bg-lattice-cyan rounded-full filter blur-xl"
                         />
                      )}
                   </div>
                   <p className="text-[10px] font-mono text-white/20 uppercase tracking-[0.5em] leading-relaxed max-w-[200px]">
                     {isReclaiming ? "Mapping sectors for 3.29 resonance..." : "Awaiting 1.0 waste reclamation Signal"}
                   </p>
                </div>
              )}
           </AnimatePresence>
        </div>
      </div>

      <div className="mt-12 p-5 rounded-2xl bg-white/5 border border-white/5 flex flex-wrap items-center justify-center gap-12">
         <div className="flex items-center gap-2">
            <Zap className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">1 * 1 = 2 Logic</span>
         </div>
         <div className="flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Waste Return Protocol</span>
         </div>
         <div className="flex items-center gap-2">
            <History className="w-3 h-3 text-white/20" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">14350 BP Zero drift</span>
         </div>
      </div>
    </div>
  );
};
